import { createFileRoute } from '@tanstack/react-router';
import { Suspense, lazy, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDashboard } from '@/api/queries';
import { Empty, ErrorBox } from '@/components/empty';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Recharts is the heaviest single dependency in the SPA (~322 KB / 82 KB
// gzip). It's only used here, so we lazy-import the chart panel; the KPI
// row and the webhook deliveries list render immediately while the
// charts chunk streams in. Vite's manualChunks rule keeps recharts +
// d3-* bundled into one cacheable `vendor-charts` chunk regardless.
const DashboardCharts = lazy(() => import('./dashboard-charts'));

export const Route = createFileRoute('/_authed/dashboard')({
  component: Dashboard,
});

const WINDOW_OPTIONS = [1, 6, 24, 72, 168, 720];

function Dashboard() {
  const { t } = useTranslation();
  const [windowHours, setWindowHours] = useState(24);
  const dash = useDashboard(windowHours);

  const { kpis } = useMemo(() => {
    const byStatus = new Map(dash.data?.payments_by_status.map((r) => [r.status, r.count]) ?? []);
    return {
      kpis: [
        { key: 'initiated', label: t('dashboard.title'), value: total(byStatus) },
        { key: 'succeeded', label: t('common.success'), value: byStatus.get('succeeded') ?? 0 },
        { key: 'failed', label: t('common.error'), value: byStatus.get('failed') ?? 0 },
        { key: 'inflight', label: t('dashboard.inflight'), value: dash.data?.inflight ?? 0 },
      ],
    };
  }, [dash.data, t]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('dashboard.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('dashboard.subtitle', { hours: windowHours })}
          </p>
        </div>
        <Select
          value={String(windowHours)}
          onValueChange={(v) => setWindowHours(Number(v))}
        >
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_OPTIONS.map((h) => (
              <SelectItem key={h} value={String(h)}>
                {h < 24 ? `${h}h` : `${h / 24}d`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </header>

      {dash.isError && <ErrorBox message={t('common.error')} />}

      <section className="grid gap-4 grid-cols-2 md:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.key}>
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{k.label}</p>
              <p className="mt-1 text-2xl font-semibold">{dash.isLoading ? '—' : k.value}</p>
            </CardContent>
          </Card>
        ))}
      </section>

      {dash.data && (
        <Suspense fallback={<ChartsSkeleton />}>
          <DashboardCharts data={dash.data} />
        </Suspense>
      )}

      <section className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.deliveries_by_status')}</CardTitle>
          </CardHeader>
          <CardContent>
            {dash.data?.deliveries_by_status.length ? (
              <ul className="space-y-1.5 text-sm">
                {dash.data.deliveries_by_status.map((r) => (
                  <li key={r.status} className="flex justify-between">
                    <span className="text-muted-foreground capitalize">{r.status}</span>
                    <span className="font-medium tabular-nums">{r.count}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty title={t('common.none')} />
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function ChartsSkeleton() {
  return (
    <section className="grid gap-4 md:grid-cols-2">
      {[0, 1].map((i) => (
        <Card key={i}>
          <CardContent className="p-4">
            <div className="h-64 rounded-md bg-muted/40 animate-pulse" />
          </CardContent>
        </Card>
      ))}
    </section>
  );
}

function total(m: Map<string, number>): number {
  let n = 0;
  for (const v of m.values()) n += v;
  return n;
}
